import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiCalendar, FiUser, FiArrowLeft, FiClock, FiShare2, FiTag } from 'react-icons/fi';
import SEO from '../components/SEO';

export default function Blog() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (slug) {
      fetchBlogPost();
    }
  }, [slug]);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch specific blog post
      const response = await fetch(`https://shop.bailebelle.com/wp-json/wp/v2/posts?slug=${slug}&_embed`);
      const data = await response.json();

      if (data && data.length > 0) {
        setBlog(data[0]);
        
        // Fetch related posts (same category or recent posts)
        const relatedResponse = await fetch(`https://shop.bailebelle.com/wp-json/wp/v2/posts?per_page=3&exclude=${data[0].id}&_embed`);
        const relatedData = await relatedResponse.json();
        setRelatedBlogs(relatedData || []);
      } else {
        setError('Blog post not found');
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
      setError('Failed to load blog post');
      
      // Fallback to sample blog post
      setBlog({
        id: 1,
        title: { rendered: "The Art of Ballet: A Timeless Expression" },
        content: { rendered: `
          <p>Ballet is more than just a dance form; it's a timeless expression of human emotion, grace, and artistic beauty. For centuries, ballet has captivated audiences worldwide with its combination of technical precision and emotional storytelling.</p>
          
          <h2>The History of Ballet</h2>
          <p>Ballet originated in the Italian Renaissance courts of the 15th century and later developed into a concert dance form in France and Russia. It has since evolved into a highly technical form of dance with its own vocabulary based on French terminology.</p>
          
          <h2>The Elements of Ballet</h2>
          <p>Ballet combines several key elements:</p>
          <ul>
            <li><strong>Technique:</strong> The foundation of ballet, requiring years of training to master</li>
            <li><strong>Artistry:</strong> The emotional expression and interpretation of music</li>
            <li><strong>Costume:</strong> The visual elements that enhance the storytelling</li>
            <li><strong>Music:</strong> The auditory foundation that guides the movement</li>
          </ul>
          
          <h2>Modern Ballet</h2>
          <p>Today, ballet continues to evolve while maintaining its classical roots. Contemporary ballet incorporates modern dance elements while preserving the technical excellence that defines the art form.</p>
          
          <p>At BaileBelle, we understand the importance of quality costumes and accessories in bringing ballet performances to life. Our carefully crafted pieces are designed to enhance the dancer's performance while maintaining the elegance and tradition of ballet.</p>
        ` },
        excerpt: { rendered: "Discover the beauty and grace of ballet as an art form that transcends time and culture." },
        date: "2024-01-15T10:00:00",
        author: 1,
        categories: [1],
        tags: [1, 2],
        _embedded: {
          author: [{ name: "BaileBelle Team" }],
          "wp:featuredmedia": [{ source_url: "https://images.unsplash.com/photo-1508700929628-666bc8bd84ea?w=800&h=600&fit=crop" }],
          "wp:term": [
            [{ name: "Dance Tips", slug: "dance-tips" }],
            [{ name: "Ballet", slug: "ballet" }, { name: "Art", slug: "art" }]
          ]
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getTitle = (blog) => {
    if (!blog || !blog.title) return '';
    if (typeof blog.title === 'string') return blog.title;
    if (blog.title.rendered) return blog.title.rendered;
    return '';
  };

  const getExcerpt = (blog) => {
    if (!blog || !blog.excerpt) return '';
    if (typeof blog.excerpt === 'string') return blog.excerpt;
    if (blog.excerpt.rendered) return blog.excerpt.rendered;
    return '';
  };

  const getContent = (blog) => {
    if (!blog || !blog.content) return '';
    if (typeof blog.content === 'string') return blog.content;
    if (blog.content.rendered) return blog.content.rendered;
    return '';
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: stripHtml(getTitle(blog)),
          text: stripHtml(getExcerpt(blog)),
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-800 rounded mb-4"></div>
            <div className="h-4 bg-gray-800 rounded w-1/3 mb-8"></div>
            <div className="h-64 bg-gray-800 rounded mb-8"></div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-800 rounded"></div>
              <div className="h-4 bg-gray-800 rounded w-5/6"></div>
              <div className="h-4 bg-gray-800 rounded w-4/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Blog Post Not Found</h1>
          <p className="text-gray-400 mb-6">The blog post you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/"
            className="px-6 py-3 bg-pink-500 text-black font-semibold rounded-lg hover:bg-pink-600 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const categories = blog._embedded?.['wp:term']?.[0] || [];
  const tags = blog._embedded?.['wp:term']?.[1] || [];

  return (
    <>
      <SEO
        title={`${stripHtml(getTitle(blog))} | BaileBelle Blog`}
        description={stripHtml(getExcerpt(blog))}
        keywords={`ballet, dance, ${tags.map(tag => tag.name).join(', ')}, BaileBelle`}
        image={blog._embedded?.['wp:featuredmedia']?.[0]?.source_url}
        url={`/blog/${slug}`}
        type="article"
      />
      
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          {/* Back Button */}
          <Link
            to="/"
            className="inline-flex items-center text-pink-500 hover:text-pink-400 mb-8 transition-colors"
          >
            <FiArrowLeft className="mr-2 w-4 h-4" />
            Back to Home
          </Link>

          {/* Article Header */}
          <header className="mb-12">
            <div className="flex items-center space-x-4 text-sm text-gray-400 mb-6">
              <div className="flex items-center space-x-1">
                <FiCalendar className="w-4 h-4" />
                <span>{formatDate(blog.date)}</span>
              </div>
              <div className="flex items-center space-x-1">
                <FiUser className="w-4 h-4" />
                <span>{blog._embedded?.author?.[0]?.name || 'BaileBelle Team'}</span>
              </div>
              <div className="flex items-center space-x-1">
                <FiClock className="w-4 h-4" />
                <span>5 min read</span>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'serif' }}>
              {stripHtml(getTitle(blog))}
            </h1>

            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              {stripHtml(getExcerpt(blog))}
            </p>

            {/* Categories and Tags */}
            <div className="flex flex-wrap items-center gap-4 mb-8">
              {categories.length > 0 && (
                <div className="flex items-center space-x-2">
                  <FiTag className="w-4 h-4 text-pink-500" />
                  <div className="flex flex-wrap gap-2">
                    {categories.map((category) => (
                      <span
                        key={category.id}
                        className="px-3 py-1 bg-pink-500/20 text-pink-400 rounded-full text-sm"
                      >
                        {category.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <button
                onClick={handleShare}
                className="flex items-center space-x-2 text-gray-400 hover:text-pink-500 transition-colors"
              >
                <FiShare2 className="w-4 h-4" />
                <span>Share</span>
              </button>
            </div>
          </header>

          {/* Featured Image */}
          {blog._embedded?.['wp:featuredmedia']?.[0]?.source_url && (
            <div className="mb-12">
              <img
                src={blog._embedded['wp:featuredmedia'][0].source_url}
                alt={stripHtml(getTitle(blog))}
                className="w-full h-64 md:h-96 object-cover rounded-2xl"
              />
            </div>
          )}

          {/* Article Content */}
          <article className="prose prose-lg prose-invert max-w-none">
            <div
              className="text-gray-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: getContent(blog) }}
            />
          </article>

          {/* Related Posts */}
          {relatedBlogs.length > 0 && (
            <section className="mt-20">
              <h2 className="text-3xl font-bold text-white mb-8" style={{ fontFamily: 'serif' }}>
                Related Posts
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedBlogs.map((relatedBlog) => (
                  <Link
                    key={relatedBlog.id}
                    to={`/blog/${relatedBlog.slug}`}
                    className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden hover:bg-gray-900/70 transition-all duration-300 hover:scale-105 group"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={
                          relatedBlog._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
                          'https://images.unsplash.com/photo-1508700929628-666bc8bd84ea?w=400&h=250&fit=crop'
                        }
                        alt={stripHtml(getTitle(relatedBlog))}
                        className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-white mb-2 line-clamp-2 group-hover:text-pink-400 transition-colors">
                        {stripHtml(getTitle(relatedBlog))}
                      </h3>
                      <p className="text-gray-400 text-sm line-clamp-3">
                        {stripHtml(getExcerpt(relatedBlog))}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}
